import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Shield, Cloud } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DeployDialog from "../components/DeployDialog";

const empty = {
  name: "", direction: "inbound", action: "deny", protocol: "tcp",
  source: "0.0.0.0/0", destination: "any", port: "443",
  priority: 100, enabled: true, cloud: "any", description: "",
};

export default function Policies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deployTarget, setDeployTarget] = useState(null); // { policy_id, name } or null
  const [configuredProviders, setConfiguredProviders] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/policies");
      setItems(data);
    } catch (err) {
      console.error("policies load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/cloud-credentials")
      .then(({ data }) => setConfiguredProviders((data || []).map((d) => d.provider)))
      .catch(() => setConfiguredProviders([]));
  }, [user]);

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/policies/${editing}`, form);
        toast.success("Policy updated");
      } else {
        await api.post("/policies", form);
        toast.success("Policy created");
      }
      setOpen(false); setEditing(null); setForm(empty);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this policy?")) return;
    try {
      await api.delete(`/policies/${id}`);
      toast.success("Policy removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (p) => {
    const { policy_id, created_at, updated_at, created_by, ...rest } = p;
    setForm(rest); setEditing(policy_id); setOpen(true);
  };

  return (
    <Layout title="Firewall Policies" subtitle="POLICY MANAGEMENT">
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest" data-testid="policies-count">
          {items.length} POLICIES · {items.filter((i) => i.enabled).length} ACTIVE
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-policy-btn" className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm gap-2">
              <Plus className="w-4 h-4" /> NEW POLICY
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /> {editing ? "Edit Policy" : "Create Policy"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Name</Label>
                <Input data-testid="policy-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-900 border-zinc-800 rounded-sm" />
              </div>
              <Field label="Direction"><Sel value={form.direction} onChange={(v) => setForm({ ...form, direction: v })} options={["inbound", "outbound"]} testid="direction-select" /></Field>
              <Field label="Action"><Sel value={form.action} onChange={(v) => setForm({ ...form, action: v })} options={["allow", "deny", "log"]} testid="action-select" /></Field>
              <Field label="Protocol"><Sel value={form.protocol} onChange={(v) => setForm({ ...form, protocol: v })} options={["tcp", "udp", "icmp", "any"]} testid="protocol-select" /></Field>
              <Field label="Cloud"><Sel value={form.cloud} onChange={(v) => setForm({ ...form, cloud: v })} options={["any", "aws", "azure", "gcp", "oracle"]} testid="cloud-select" /></Field>
              <Field label="Source"><Input data-testid="source-input" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></Field>
              <Field label="Destination"><Input data-testid="destination-input" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} /></Field>
              <Field label="Port"><Input data-testid="port-input" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} /></Field>
              <Field label="Priority"><Input data-testid="priority-input" type="number" className="bg-zinc-900 border-zinc-800 rounded-sm font-mono" value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "0", 10) })} /></Field>
              <div className="col-span-2 flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2 bg-zinc-900">
                <div>
                  <div className="text-sm">Enabled</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">applies on save</div>
                </div>
                <Switch data-testid="enabled-switch" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="submit-policy-btn" onClick={submit} disabled={busy} className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-sm">
                {busy ? "..." : editing ? "SAVE" : "CREATE"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-800 rounded-sm bg-zinc-900/40 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 bg-zinc-900/60 border-b border-zinc-800">
            <tr>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Direction</th>
              <th className="text-left px-4 py-3">Action</th>
              <th className="text-left px-4 py-3">Proto</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Dest</th>
              <th className="text-left px-4 py-3">Port</th>
              <th className="text-left px-4 py-3">Cloud</th>
              <th className="text-left px-4 py-3">State</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="policies-table">
            {items.map((p) => (
              <tr key={p.policy_id} className="border-b border-zinc-800/70 hover:bg-zinc-900/60" data-testid={`policy-row-${p.policy_id}`}>
                <td className="px-4 py-3 font-mono text-cyan-400">{p.priority}</td>
                <td className="px-4 py-3">{p.name}</td>
                <td className="px-4 py-3 font-mono text-xs uppercase">{p.direction}</td>
                <td className="px-4 py-3">
                  <span className={
                    "px-1.5 py-0.5 border rounded-sm uppercase font-mono text-[10px] tracking-widest " +
                    (p.action === "allow" ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
                      : p.action === "deny" ? "text-red-400 border-red-500/40 bg-red-500/10"
                      : "text-amber-400 border-amber-500/40 bg-amber-500/10")
                  }>{p.action}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs uppercase text-zinc-300">{p.protocol}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-300">{p.source}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-300">{p.destination}</td>
                <td className="px-4 py-3 font-mono text-xs text-cyan-300">{p.port}</td>
                <td className="px-4 py-3 font-mono text-xs uppercase text-zinc-400">{p.cloud}</td>
                <td className="px-4 py-3">
                  <span className={"inline-flex items-center gap-1.5 text-[10px] font-mono uppercase " + (p.enabled ? "text-emerald-400" : "text-zinc-500")}>
                    <span className={p.enabled ? "live-dot" : "live-dot amber"} />
                    {p.enabled ? "ACTIVE" : "DISABLED"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {user?.role === "admin" && (
                    <button
                      data-testid={`deploy-policy-${p.policy_id}`}
                      onClick={() => setDeployTarget({ policy_id: p.policy_id, name: p.name })}
                      className="px-2 py-1 text-zinc-400 hover:text-cyan-400"
                      title="Deploy to clouds"
                    >
                      <Cloud className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button data-testid={`edit-policy-${p.policy_id}`} onClick={() => startEdit(p)} className="px-2 py-1 text-zinc-400 hover:text-cyan-400">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {user?.role === "admin" && (
                    <button data-testid={`delete-policy-${p.policy_id}`} onClick={() => del(p.policy_id)} className="px-2 py-1 text-zinc-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={11} className="text-center text-zinc-500 py-10 font-mono">no policies. create one to begin.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {deployTarget && (
        <DeployDialog
          open={!!deployTarget}
          onOpenChange={(v) => { if (!v) setDeployTarget(null); }}
          mode="policy-to-clouds"
          policyIds={[deployTarget.policy_id]}
          policyName={deployTarget.name}
          configuredProviders={configuredProviders}
        />
      )}
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">{label}</Label>
      {children}
    </div>
  );
}

function Sel({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-zinc-900 border-zinc-800 rounded-sm font-mono uppercase text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-zinc-950 border-zinc-800">
        {options.map((o) => (
          <SelectItem key={o} value={o} className="font-mono uppercase text-xs">{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
